<template>
  <div class="min-h-screen bg-[#F8FAFC] flex flex-col font-sans">

    <!-- Page d'accueil -->
    <HomePage v-if="view === 'home'" @open-app="goTo('app')" />

    <!-- Helpdesk -->
    <template v-else>

      <!-- Header GiraafDesk -->
      <header class="bg-white border-b border-slate-200/80 sticky top-0 z-30 shadow-xs">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <!-- "Desk" : dégradé en CSS en ligne (fonctionne quelle que soit la version de Tailwind).
               L'ordre compte : "background" avant "background-clip", sinon le raccourci écrase le clip. -->
            <button type="button" @click="goTo('home')" title="Retour à l'accueil"
              class="text-xl font-extrabold text-[#0F172A] tracking-tight cursor-pointer">
              Giraaf<span
                style="background: linear-gradient(135deg, #0284C7 0%, #4F46E5 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;">Desk</span>
            </button>
            <span class="text-[11px] bg-sky-50 text-sky-700 font-bold px-2.5 py-0.5 rounded-full border border-sky-100">
              Helpdesk & Support
            </span>
          </div>

          <button @click="showCreateModal = true"
            style="background: linear-gradient(135deg, #0284C7 0%, #2563EB 50%, #4F46E5 100%);"
            class="flex items-center gap-2 text-white font-bold text-xs sm:text-sm px-4 py-2 rounded-xl shadow-xs hover:opacity-95 transition cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4" />
            </svg>
            Nouveau ticket
          </button>
        </div>
      </header>

      <!-- Contenu Principal -->
      <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grow w-full space-y-6">

        <!-- Métriques Rapides -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
            <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Total Tickets</span>
            <p class="text-2xl font-extrabold text-[#0F172A] mt-1">{{ tickets.length }}</p>
          </div>
          <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
            <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Ouverts</span>
            <p class="text-2xl font-extrabold text-amber-500 mt-1">{{ countStatus('Ouvert') }}</p>
          </div>
          <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
            <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">En attente</span>
            <p class="text-2xl font-extrabold text-sky-600 mt-1">{{ countStatus('En attente') }}</p>
          </div>
          <div class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
            <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Résolus / Fermés</span>
            <p class="text-2xl font-extrabold text-emerald-600 mt-1">{{ countStatus('Fermé') + countStatus('Résolu') }}
            </p>
          </div>
        </div>

        <!-- Filtres & Recherche -->
        <div
          class="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4">
          <div class="flex flex-wrap items-center gap-1.5 w-full sm:w-auto">
            <button v-for="st in ['Tous', 'Ouvert', 'En attente', 'Résolu', 'Fermé']" :key="st"
              @click="filterStatus = st" class="px-3 py-1.5 rounded-xl text-xs font-semibold transition"
              :class="filterStatus === st ? 'bg-[#0F172A] text-white shadow-xs' : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'">
              {{ st }}
            </button>
          </div>

          <div class="relative w-full sm:w-72">
            <input v-model="searchQuery" type="text" placeholder="Rechercher par numéro, sujet..."
              class="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-sky-500 outline-none text-xs text-slate-800 transition" />
            <svg class="w-4 h-4 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        <!-- Table des Tickets -->
        <div class="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div v-if="isLoading" class="py-16 text-center text-sm text-slate-400">
            Chargement des tickets...
          </div>

          <!-- Échec du chargement : on ne le confond pas avec « aucun ticket » -->
          <div v-else-if="loadError" class="py-16 px-6 text-center">
            <p class="text-sm font-bold text-[#0F172A]">Impossible de charger les tickets.</p>
            <p class="mt-1 text-xs text-[#64748B]">Vérifiez votre connexion, puis réessayez.</p>
            <button type="button" @click="fetchTickets"
              class="mt-4 px-4 py-2 rounded-xl border border-[#CBD5E1] text-xs font-bold text-[#0F172A] hover:border-[#94A3B8] transition cursor-pointer">
              Réessayer
            </button>
          </div>

          <!-- Premier passage : aucun ticket n'existe encore -->
          <div v-else-if="tickets.length === 0" class="py-16 px-6 text-center">
            <div class="w-12 h-12 mx-auto rounded-2xl flex items-center justify-center"
              style="background: #E0F2FE; color: #0284C7;" aria-hidden="true">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h2 class="mt-4 text-base font-bold text-[#0F172A]">Aucun ticket pour le moment</h2>
            <p class="mt-1 text-sm text-[#64748B] max-w-sm mx-auto leading-relaxed">
              Quand un client vous contacte, ouvrez un ticket pour suivre sa demande jusqu'à sa résolution.
            </p>
            <button type="button" @click="showCreateModal = true"
              style="background: linear-gradient(135deg, #0284C7 0%, #2563EB 50%, #4F46E5 100%);"
              class="mt-5 text-white font-bold text-sm px-5 py-2.5 rounded-xl shadow-xs hover:opacity-95 transition cursor-pointer">
              Créer le premier ticket
            </button>
          </div>

          <!-- Des tickets existent, mais aucun ne correspond au filtre ou à la recherche -->
          <div v-else-if="filteredTickets.length === 0" class="py-16 px-6 text-center">
            <p class="text-sm font-bold text-[#0F172A]">Aucun ticket ne correspond à votre recherche.</p>
            <button type="button" @click="resetFilters"
              class="mt-3 text-xs font-bold text-[#0284C7] hover:text-[#075985] transition cursor-pointer">
              Réinitialiser les filtres
            </button>
          </div>

          <div v-else class="overflow-x-auto">
            <table class="w-full text-left text-xs text-slate-600">
              <thead class="bg-slate-50 text-[11px] font-bold text-slate-400 uppercase border-b border-slate-100">
                <tr>
                  <th class="py-3.5 px-4">Numéro</th>
                  <th class="py-3.5 px-4">Sujet & Description</th>
                  <th class="py-3.5 px-4">Client</th>
                  <th class="py-3.5 px-4">Priorité</th>
                  <th class="py-3.5 px-4">Statut</th>
                  <th class="py-3.5 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <tr v-for="ticket in filteredTickets" :key="ticket.id" class="hover:bg-sky-50/30 transition">
                  <td class="py-3.5 px-4 font-mono font-bold text-slate-900">
                    {{ ticket.ticket_number }}
                  </td>
                  <td class="py-3.5 px-4 max-w-xs">
                    <p class="font-bold text-slate-900 truncate">{{ ticket.subject }}</p>
                    <p class="text-[11px] text-slate-400 truncate mt-0.5">{{ ticket.description }}</p>
                  </td>
                  <td class="py-3.5 px-4 text-slate-500 font-medium">
                    {{ ticket.client_email }}
                  </td>
                  <td class="py-3.5 px-4">
                    <span class="px-2.5 py-0.5 rounded-full font-semibold text-[10px]"
                      :class="priorityBadgeClass(ticket.priority)">
                      {{ ticket.priority || 'Normale' }}
                    </span>
                  </td>
                  <td class="py-3.5 px-4">
                    <div
                      class="relative inline-block rounded-full border transition focus-within:ring-2 focus-within:ring-[#0284C7]/30"
                      :class="statusBadgeClass(ticket.status)">
                      <select :value="ticket.status" @change="updateStatus(ticket, $event.target.value)"
                        aria-label="Changer le statut" style="color: inherit;"
                        class="appearance-none bg-transparent text-[11px] font-bold pl-3 pr-7 py-1.5 outline-none cursor-pointer">
                        <option value="Ouvert" class="bg-white text-[#0F172A]">Ouvert</option>
                        <option value="En attente" class="bg-white text-[#0F172A]">En attente</option>
                        <option value="Résolu" class="bg-white text-[#0F172A]">Résolu</option>
                        <option value="Fermé" class="bg-white text-[#0F172A]">Fermé</option>
                      </select>
                      <svg class="w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </td>
                  <td class="py-3.5 px-4 text-right">
                    <button @click="openDetail(ticket)"
                      class="text-xs font-bold text-sky-600 hover:text-sky-800 transition cursor-pointer">
                      Consulter →
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </main>

      <!-- Modal Nouveau Ticket -->
      <div v-if="showCreateModal"
        class="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4 z-50">
        <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-100 space-y-4">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-bold text-slate-900">Ouvrir un nouveau ticket</h2>
            <button @click="showCreateModal = false"
              class="text-slate-400 hover:text-slate-600 text-lg cursor-pointer">✕</button>
          </div>

          <form @submit.prevent="createTicket" class="space-y-3 text-xs">
            <div>
              <label class="block font-semibold text-slate-600 mb-1">Email client *</label>
              <input v-model="newTicket.client_email" type="email" required placeholder="client@entreprise.com"
                class="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-sky-500 outline-none" />
            </div>

            <div>
              <label class="block font-semibold text-slate-600 mb-1">Sujet de l'incident *</label>
              <input v-model="newTicket.subject" type="text" required placeholder="ex: Erreur de facturation"
                class="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-sky-500 outline-none" />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block font-semibold text-slate-600 mb-1">Urgence</label>
                <select v-model="newTicket.priority"
                  class="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-sky-500 outline-none">
                  <option value="Basse">Basse</option>
                  <option value="Normale">Normale</option>
                  <option value="Haute">Haute</option>
                  <option value="Urgente">Urgente</option>
                </select>
              </div>
              <div>
                <label class="block font-semibold text-slate-600 mb-1">Statut initial</label>
                <select v-model="newTicket.status"
                  class="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-sky-500 outline-none">
                  <option value="Ouvert">Ouvert</option>
                  <option value="En attente">En attente</option>
                </select>
              </div>
            </div>

            <div>
              <label class="block font-semibold text-slate-600 mb-1">Description *</label>
              <textarea v-model="newTicket.description" rows="4" required placeholder="Détails du problème..."
                class="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-sky-500 outline-none"></textarea>
            </div>

            <div class="flex justify-end gap-2 pt-2">
              <button type="button" @click="showCreateModal = false"
                class="px-4 py-2 font-semibold text-slate-500 hover:text-slate-700">Annuler</button>
              <button type="submit" style="background: linear-gradient(135deg, #0284C7 0%, #2563EB 50%, #4F46E5 100%);"
                class="text-white font-bold px-5 py-2.5 rounded-xl shadow-xs hover:opacity-95">Créer le ticket</button>
            </div>
          </form>
        </div>
      </div>

      <!-- Modal Détail & Résolution du Ticket (Inspiré de detail_ticket.php) -->
      <div v-if="selectedTicket"
        class="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4 z-50">
        <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-xl w-full shadow-2xl border border-slate-100 space-y-4">
          <div class="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <span class="font-mono text-xs font-bold text-sky-600">{{ selectedTicket.ticket_number }}</span>
              <h2 class="text-base font-bold text-slate-900 mt-0.5">{{ selectedTicket.subject }}</h2>
            </div>
            <button @click="selectedTicket = null"
              class="text-slate-400 hover:text-slate-600 text-lg cursor-pointer">✕</button>
          </div>

          <div class="space-y-4 text-xs">
            <div class="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
              <div>
                <span class="text-slate-400 block text-[10px] uppercase font-bold">Email Demandeur</span>
                <span class="font-bold text-slate-900">{{ selectedTicket.client_email }}</span>
              </div>
              <div>
                <span class="text-slate-400 block text-[10px] uppercase font-bold">Statut Actuel</span>
                <span class="inline-block mt-0.5 px-2.5 py-0.5 rounded-full border text-[11px] font-bold"
                  :class="statusBadgeClass(selectedTicket.status)">{{ selectedTicket.status
                  }}</span>
              </div>
            </div>

            <div>
              <span class="text-slate-400 font-semibold block mb-1 text-[11px] uppercase">Message Initial :</span>
              <p class="bg-slate-50 p-3.5 rounded-xl text-slate-700 leading-relaxed border border-slate-100">
                {{ selectedTicket.description }}
              </p>
            </div>

            <!-- Section Résolution & Réponse (Héritée de detail_ticket.php) -->
            <div class="pt-2 border-t border-slate-100 space-y-2">
              <span class="text-slate-900 font-bold block text-xs">Réponse de résolution / Note technique :</span>
              <textarea v-model="resolutionNote" rows="3" placeholder="Expliquez la solution apportée au client..."
                class="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-sky-500 outline-none text-xs"></textarea>

              <div class="flex justify-between items-center pt-1">
                <span class="text-[11px] text-slate-400">
                  Créé le : {{ formatDate(selectedTicket.created_at) }}
                </span>
                <button @click="saveResolution('Résolu')"
                  class="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition cursor-pointer">
                  Résoudre le ticket ✓
                </button>
              </div>
            </div>
          </div>

          <div class="flex justify-end pt-2 border-t border-slate-100">
            <button @click="selectedTicket = null"
              class="bg-slate-100 text-slate-600 hover:bg-slate-200 font-bold text-xs px-4 py-2 rounded-xl transition cursor-pointer">
              Fermer
            </button>
          </div>
        </div>
      </div>

    </template>

    <!-- Footer commun -->
    <AppFooter @navigate="goTo" />

  </div>
</template>

<script>
import apiClient from '@/services/apiClient'
import HomePage from '@/components/HomePage.vue'
import AppFooter from '@/components/AppFooter.vue'

export default {
  name: 'App',
  components: { HomePage, AppFooter },
  data() {
    return {
      // 'home' = page d'accueil, 'app' = helpdesk (adresse #/helpdesk)
      view: window.location.hash === '#/helpdesk' ? 'app' : 'home',
      tickets: [],
      isLoading: false,
      loadError: false,
      filterStatus: 'Tous',
      searchQuery: '',
      showCreateModal: false,
      selectedTicket: null,
      resolutionNote: '',
      newTicket: {
        client_email: '',
        subject: '',
        priority: 'Normale',
        status: 'Ouvert',
        description: ''
      }
    }
  },
  computed: {
    filteredTickets() {
      return this.tickets.filter(t => {
        const matchesStatus = this.filterStatus === 'Tous' || t.status === this.filterStatus
        const q = this.searchQuery.toLowerCase()
        const matchesSearch = !q ||
          t.ticket_number?.toLowerCase().includes(q) ||
          t.subject?.toLowerCase().includes(q) ||
          t.client_email?.toLowerCase().includes(q)
        return matchesStatus && matchesSearch
      })
    }
  },
  watch: {
    view(value) {
      if (value === 'app') this.fetchTickets()
    }
  },
  methods: {
    goTo(view, section = null) {
      if (view !== this.view) {
        this.view = view
        const url = view === 'app' ? '#/helpdesk' : window.location.pathname + window.location.search
        window.history.pushState(null, '', url)
      }
      this.$nextTick(() => {
        const el = section ? document.getElementById(section) : null
        if (el) el.scrollIntoView({ behavior: 'smooth' })
        else window.scrollTo({ top: 0 })
      })
    },
    onPopState() {
      this.view = window.location.hash === '#/helpdesk' ? 'app' : 'home'
    },
    resetFilters() {
      this.filterStatus = 'Tous'
      this.searchQuery = ''
    },
    async fetchTickets() {
      this.isLoading = true
      this.loadError = false
      try {
        const res = await apiClient.get('/api/v1/tickets')
        this.tickets = res.data
      } catch (err) {
        console.error('Erreur tickets:', err)
        this.loadError = true
      } finally {
        this.isLoading = false
      }
    },
    async createTicket() {
      try {
        await apiClient.post('/api/v1/tickets', { ticket: this.newTicket })
        this.showCreateModal = false
        this.newTicket = { client_email: '', subject: '', priority: 'Normale', status: 'Ouvert', description: '' }
        await this.fetchTickets()
      } catch (err) {
        console.error('Erreur création ticket:', err)
      }
    },
    async updateStatus(ticket, newStatus) {
      ticket.status = newStatus
      try {
        await apiClient.patch(`/api/v1/tickets/${ticket.id}`, { ticket: { status: newStatus } })
      } catch (err) {
        console.error('Erreur update:', err)
      }
    },
    openDetail(ticket) {
      this.selectedTicket = ticket
      this.resolutionNote = ticket.resolution_note || ''
    },
    async saveResolution(newStatus) {
      if (!this.selectedTicket) return
      try {
        await apiClient.patch(`/api/v1/tickets/${this.selectedTicket.id}`, {
          ticket: {
            status: newStatus,
            resolution_note: this.resolutionNote
          }
        })
        this.selectedTicket.status = newStatus
        this.selectedTicket.resolution_note = this.resolutionNote
        await this.fetchTickets()
        this.selectedTicket = null
      } catch (err) {
        console.error('Erreur sauvegarde résolution:', err)
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },
    countStatus(statusName) {
      return this.tickets.filter(t => t.status === statusName).length
    },
    priorityBadgeClass(priority) {
      const map = {
        Urgente: 'bg-[#FFE4E6] text-[#BE123C] font-bold',
        Haute: 'bg-[#FEF3C7] text-[#92400E] font-bold',
        Normale: 'bg-[#E0F2FE] text-[#0369A1]',
        Basse: 'bg-[#F1F5F9] text-[#475569]'
      }
      return map[priority] || 'bg-[#F1F5F9] text-[#475569]'
    },
    statusBadgeClass(status) {
      const map = {
        Ouvert: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]',
        'En attente': 'bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]',
        Résolu: 'bg-[#D1FAE5] text-[#047857] border-[#A7F3D0]',
        Fermé: 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'
      }
      return map[status] || 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'
    }
  },
  mounted() {
    window.addEventListener('popstate', this.onPopState)
    if (this.view === 'app') this.fetchTickets()
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.onPopState)
  }
}
</script>
